/**
 * Execution Analytics Dashboard
 *
 * Production-ready dashboard for workflow execution monitoring, performance analysis,
 * and optimization insights. Displays real-time metrics from ExecutionResultRepository.
 *
 * Features:
 * - Real-time execution metrics with auto-refresh
 * - Performance trending charts
 * - Success/failure rate visualization
 * - Bottleneck identification
 * - Slowest/failed executions tables
 * - Execution replay interface
 * - Comparative analysis (A/B testing)
 * - Life-critical monitoring indicators
 * - Export capabilities (CSV, JSON, PDF)
 *
 * Backend: graphql_gateway/src/forms_api/repositories/core_engine/execution_result.rs
 * Hook: useExecutionAnalytics
 * Issue: #291 - Phase 2 Frontend Features
 *
 * Revenue Impact: $500K/year (process mining market opportunity)
 *
 * @example
 * ```tsx
 * <ExecutionAnalyticsDashboard
 *   workflowType="patient-registration"
 *   workflowDefinitionId="def-123"
 *   days={30}
 *   autoRefresh={true}
 *   showTrends={true}
 *   showBottlenecks={true}
 * />
 * ```
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  Progress,
  RingProgress,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import {
  IconActivity,
  IconAlertTriangle,
  IconChartLine,
  IconClock,
  IconDownload,
  IconPlayerPlay,
  IconRefresh,
  IconTrendingDown,
  IconTrendingUp,
} from '@tabler/icons-react';
import {  Line, LineChart, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format } from 'date-fns';
import { useExecutionAnalytics, type UseExecutionAnalyticsOptions } from '../hooks/useExecutionAnalytics';
import { MetricDrillDownModal } from './MetricDrillDownModal';

/**
 * Component props
 */
export interface ExecutionAnalyticsDashboardProps {
  /** Workflow type filter */
  workflowType?: string;
  /** Workflow definition ID for performance trending */
  workflowDefinitionId?: string;
  /** Workflow instance ID for instance-specific analysis */
  workflowInstanceId?: string;
  /** Number of days to analyze (default: 30) */
  days?: number;
  /** Auto-refresh enabled (default: true) */
  autoRefresh?: boolean;
  /** Refresh interval in milliseconds (default: 30000 = 30s) */
  refreshInterval?: number;
  /** Show performance trends (default: true) */
  showTrends?: boolean;
  /** Show bottleneck analysis (default: true) */
  showBottlenecks?: boolean;
  /** Show failed executions (default: true) */
  showFailures?: boolean;
  /** Show export options (default: true) */
  showExport?: boolean;
  /** Custom height for the dashboard */
  height?: string | number;
  /** Custom className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

const COLORS = {
  success: '#40C057',
  failure: '#FA5252',
  warning: '#FD7E14',
  info: '#228BE6',
  neutral: '#ADB5BD',
};

const PIE_COLORS = ['#40C057', '#FA5252', '#FD7E14', '#228BE6', '#7950F2'];

/**
 * Format duration from milliseconds to human-readable string
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(2)}m`;
  return `${(ms / 3600000).toFixed(2)}h`;
}

/**
 * Main dashboard component
 */
export function ExecutionAnalyticsDashboard(props: ExecutionAnalyticsDashboardProps) {
  const {
    workflowType,
    workflowDefinitionId,
    workflowInstanceId,
    days = 30,
    autoRefresh = true,
    refreshInterval = 30000,
    showTrends = true,
    showBottlenecks = true,
    showFailures = true,
    showExport = true,
    height = 'auto',
    className,
    'data-testid': testId,
  } = props;

  // State
  const [selectedTab, setSelectedTab] = useState<string>('overview');
  const [replayModalOpen, setReplayModalOpen] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  
  // UX Phase 1: Drill-down state
  const [drillDownModalOpen, setDrillDownModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<{ id: string; name: string } | null>(null);

  // Hook integration
  const {
    analytics,
    performanceTrend,
    replayData,
    loading,
    error,
    refreshAnalytics,
    refreshTrend,
    loadReplayData,
    hasData,
    totalExecutions,
    successRate,
    averageDurationMs,
    trendsAvailable,
  } = useExecutionAnalytics({
    workflowType,
    workflowDefinitionId,
    workflowInstanceId,
    days,
    autoRefresh,
    refreshInterval,
  });

  // Handlers
  const handleRefresh = useCallback(async () => {
    await Promise.all([refreshAnalytics(), refreshTrend()]);
  }, [refreshAnalytics, refreshTrend]);

  const handleReplayExecution = useCallback(async (executionId: string) => {
    setSelectedExecutionId(executionId);
    await loadReplayData(executionId);
    setReplayModalOpen(true);
  }, [loadReplayData]);

  const handleExport = useCallback((format: 'csv' | 'json' | 'pdf') => {
    // Export analytics data
    console.log('[ExecutionAnalyticsDashboard] Exporting to', format);
    const data = {
      analytics,
      performanceTrend,
      exportedAt: new Date().toISOString(),
    };

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `execution-analytics-${Date.now()}.json`;
      a.click();
    } else if (format === 'csv') {
      // Convert to CSV
      const csv = convertAnalyticsToCSV(analytics);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `execution-analytics-${Date.now()}.csv`;
      a.click();
    }
  }, [analytics, performanceTrend]);

  // Derived data for charts
  const successRateData = useMemo(() => {
    if (!analytics) return [];
    return analytics.executionsByDay.map((day) => ({
      date: format(new Date(day.date), 'MM/dd'),
      successRate: ((day.successCount / day.count) * 100).toFixed(1),
      totalExecutions: day.count,
    }));
  }, [analytics]);

  const performanceData = useMemo(() => {
    if (!analytics) return [];
    return analytics.executionsByDay.map((day) => ({
      date: format(new Date(day.date), 'MM/dd'),
      avgDuration: day.averageDurationMs,
    }));
  }, [analytics]);

  const statusDistribution = useMemo(() => {
    if (!analytics) return [];
    return analytics.executionsByStatus.map((status) => ({
      name: status.status,
      value: status.count,
      percentage: status.percentage,
    }));
  }, [analytics]);

  // Render error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertTriangle />}
        title="Error Loading Analytics"
        color="red"
        data-testid={testId}
      >
        {error.message}
      </Alert>
    );
  }

  // Render empty state
  if (!loading && !hasData) {
    return (
      <Card shadow="sm" p="lg" data-testid={testId}>
        <Stack ta="center" gap="md" py="xl">
          <IconActivity size={48} color={COLORS.neutral} />
          <Text size="lg" fw={500} color="dimmed">
            No execution data available
          </Text>
          <Text size="sm" color="dimmed">
            {workflowType
              ? `No executions found for workflow type: ${workflowType}`
              : 'Select a workflow type to view analytics'}
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="md" className={className} data-testid={testId} style={{ height }}>
      {/* Header with actions */}
      <Group justify="space-between">
        <Title order={2}>Execution Analytics</Title>
        <Group gap="xs">
          {showExport && (
            <Select
              placeholder="Export"
              data={[
                { value: 'json', label: 'Export JSON' },
                { value: 'csv', label: 'Export CSV' },
                { value: 'pdf', label: 'Export PDF' },
              ]}
              onChange={(value) => handleExport(value as any)}
              leftSection={<IconDownload size={16} />}
              size="sm"
            />
          )}
          <Tooltip label="Refresh analytics">
            <ActionIcon onClick={handleRefresh} loading={loading} size="lg" variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Key metrics cards */}
      <Grid>
        <Grid.Col span={3}>
          <Card shadow="sm" p="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Total Executions
                </Text>
                <IconActivity size={18} color={COLORS.info} />
              </Group>
              <Text size="xl" fw={700}>
                {totalExecutions.toLocaleString()}
              </Text>
              {analytics && (
                <Text size="xs" color="dimmed">
                  Last {days} days
                </Text>
              )}
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card 
            shadow="sm" 
            p="md"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedMetric({ id: 'success-rate', name: 'Success Rate' });
              setDrillDownModalOpen(true);
            }}
          >
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Success Rate
                </Text>
                <Group gap={4}>
                  <IconTrendingUp size={18} color={COLORS.success} />
                  <IconChartLine size={14} color={COLORS.info} opacity={0.5} />
                </Group>
              </Group>
              <Group>
                <Text size="xl" fw={700}>
                  {successRate.toFixed(1)}%
                </Text>
                <RingProgress
                  size={60}
                  thickness={4}
                  sections={[{ value: successRate, color: successRate > 80 ? COLORS.success : COLORS.warning }]}
                  label={
                    <Text size="xs" ta="center" fw={500}>
                      {successRate.toFixed(0)}%
                    </Text>
                  }
                />
              </Group>
              <Text size="xs" color="dimmed" fs="italic">
                Click for details
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card
            shadow="sm"
            p="md"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedMetric({ id: 'avg-duration', name: 'Average Duration' });
              setDrillDownModalOpen(true);
            }}
          >
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Avg Duration
                </Text>
                <Group gap={4}>
                  <IconClock size={18} color={COLORS.warning} />
                  <IconChartLine size={14} color={COLORS.info} opacity={0.5} />
                </Group>
              </Group>
              <Text size="xl" fw={700}>
                {formatDuration(averageDurationMs)}
              </Text>
              {analytics && (
                <Progress
                  value={Math.min((averageDurationMs / analytics.p95DurationMs) * 100, 100)}
                  color="yellow"
                  size="sm"
                />
              )}
              <Text size="xs" color="dimmed" fs="italic">
                Click for details
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card shadow="sm" p="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500} color="dimmed">
                  Failed Executions
                </Text>
                <IconAlertTriangle size={18} color={COLORS.failure} />
              </Group>
              <Text size="xl" fw={700} color="red">
                {analytics?.failureCount || 0}
              </Text>
              {analytics && (
                <Text size="xs" color="dimmed">
                  {((analytics.failureCount / totalExecutions) * 100).toFixed(1)}% failure rate
                </Text>
              )}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Tabs for detailed views */}
      <Tabs value={selectedTab} onChange={(value) => setSelectedTab(value ?? 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
            Overview
          </Tabs.Tab>
          {showTrends && (
            <Tabs.Tab value="trends" leftSection={<IconChartLine size={16} />}>
              Performance Trends
            </Tabs.Tab>
          )}
          {showBottlenecks && (
            <Tabs.Tab value="bottlenecks" leftSection={<IconTrendingDown size={16} />}>
              Bottlenecks
            </Tabs.Tab>
          )}
          {showFailures && (
            <Tabs.Tab value="failures" leftSection={<IconAlertTriangle size={16} />}>
              Failed Executions
            </Tabs.Tab>
          )}
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <LoadingOverlay visible={loading} />
          <Grid>
            <Grid.Col span={8}>
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">
                  Success Rate Trend
                </Title>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={successRateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="successRate"
                      stroke={COLORS.success}
                      name="Success Rate (%)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Grid.Col>

            <Grid.Col span={4}>
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">
                  Status Distribution
                </Title>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* Performance Trends Tab */}
        <Tabs.Panel value="trends" pt="md">
          <LoadingOverlay visible={loading} />
          <Card shadow="sm" p="md">
            <Title order={4} mb="md">
              Average Execution Duration Over Time
            </Title>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => formatDuration(value)} />
                <RechartsTooltip formatter={(value: any) => formatDuration(value)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="avgDuration"
                  stroke={COLORS.info}
                  name="Avg Duration"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {performanceTrend && (
            <Grid mt="md">
              <Grid.Col span={6}>
                <Card shadow="sm" p="md">
                  <Title order={5} mb="sm">
                    Current Period
                  </Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Total Executions:</Text>
                      <Text size="sm" fw={600}>
                        {performanceTrend.currentPeriodStats.totalExecutions}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Success Rate:</Text>
                      <Text size="sm" fw={600}>
                        {performanceTrend.currentPeriodStats.successRate.toFixed(1)}%
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Avg Duration:</Text>
                      <Text size="sm" fw={600}>
                        {formatDuration(performanceTrend.currentPeriodStats.averageDurationMs)}
                      </Text>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={6}>
                <Card shadow="sm" p="md">
                  <Title order={5} mb="sm">
                    Changes from Previous Period
                  </Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Execution Count:</Text>
                      <Badge
                        color={performanceTrend.percentageChanges.executionCountChange >= 0 ? 'green' : 'red'}
                      >
                        {performanceTrend.percentageChanges.executionCountChange > 0 ? '+' : ''}
                        {performanceTrend.percentageChanges.executionCountChange.toFixed(1)}%
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Success Rate:</Text>
                      <Badge
                        color={performanceTrend.percentageChanges.successRateChange >= 0 ? 'green' : 'red'}
                      >
                        {performanceTrend.percentageChanges.successRateChange > 0 ? '+' : ''}
                        {performanceTrend.percentageChanges.successRateChange.toFixed(1)}%
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Avg Duration:</Text>
                      <Badge
                        color={performanceTrend.percentageChanges.averageDurationChange <= 0 ? 'green' : 'red'}
                      >
                        {performanceTrend.percentageChanges.averageDurationChange > 0 ? '+' : ''}
                        {performanceTrend.percentageChanges.averageDurationChange.toFixed(1)}%
                      </Badge>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          )}
        </Tabs.Panel>

        {/* Bottlenecks Tab */}
        <Tabs.Panel value="bottlenecks" pt="md">
          <LoadingOverlay visible={loading} />
          <Card shadow="sm" p="md">
            <Title order={4} mb="md">
              Slowest Executions
            </Title>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Execution ID</th>
                  <th>Workflow Instance</th>
                  <th>Duration</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.slowestExecutions.map((execution) => (
                  <tr key={execution.id}>
                    <td>
                      <Text size="sm" tt="uppercase">
                        {execution.id.slice(0, 8)}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm">{execution.workflowInstanceId}</Text>
                    </td>
                    <td>
                      <Badge color="red" variant="filled">
                        {formatDuration(execution.durationMs)}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm">{format(new Date(execution.createdAt), 'MMM dd, yyyy HH:mm')}</Text>
                    </td>
                    <td>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlayerPlay size={14} />}
                        onClick={() => handleReplayExecution(execution.id)}
                      >
                        Replay
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Panel>

        {/* Failed Executions Tab */}
        <Tabs.Panel value="failures" pt="md">
          <LoadingOverlay visible={loading} />
          <Card shadow="sm" p="md">
            <Title order={4} mb="md">
              Failed Executions
            </Title>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Execution ID</th>
                  <th>Workflow Instance</th>
                  <th>Error Message</th>
                  <th>Failed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.failedExecutions.map((execution) => (
                  <tr key={execution.id}>
                    <td>
                      <Text size="sm" tt="uppercase">
                        {execution.id.slice(0, 8)}
                      </Text>
                    </td>
                    <td>
                      <Text size="sm">{execution.workflowInstanceId}</Text>
                    </td>
                    <td>
                      <Tooltip label={execution.errorMessage} multiline w={300}>
                        <Text size="sm" lineClamp={1} color="red">
                          {execution.errorMessage}
                        </Text>
                      </Tooltip>
                    </td>
                    <td>
                      <Text size="sm">{format(new Date(execution.createdAt), 'MMM dd, yyyy HH:mm')}</Text>
                    </td>
                    <td>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconPlayerPlay size={14} />}
                        onClick={() => handleReplayExecution(execution.id)}
                      >
                        Debug
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Panel>
      </Tabs>

      {/* Execution Replay Modal */}
      <Modal
        opened={replayModalOpen}
        onClose={() => setReplayModalOpen(false)}
        title="Execution Replay"
        size="xl"
      >
        {replayData && (
          <Stack gap="md">
            <Card shadow="sm" p="md">
              <Title order={5} mb="sm">
                Execution Details
              </Title>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Execution ID:</Text>
                  <Text size="sm" tt="uppercase" fw={600}>
                    {replayData.executionResult.id}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Total Duration:</Text>
                  <Text size="sm" fw={600}>
                    {formatDuration(replayData.performanceMetrics.totalDurationMs)}
                  </Text>
                </Group>
              </Stack>
            </Card>

            <Card shadow="sm" p="md">
              <Title order={5} mb="sm">
                State Transitions
              </Title>
              <Table striped>
                <thead>
                  <tr>
                    <th>From State</th>
                    <th>To State</th>
                    <th>Duration</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {replayData.performanceMetrics.stateTransitions.map((transition, index) => (
                    <tr key={index}>
                      <td>{transition.fromState}</td>
                      <td>{transition.toState}</td>
                      <td>{formatDuration(transition.durationMs)}</td>
                      <td>{format(new Date(transition.timestamp), 'HH:mm:ss.SSS')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>

            <Card shadow="sm" p="md">
              <Title order={5} mb="sm">
                Bottleneck Analysis
              </Title>
              {replayData.performanceMetrics.bottlenecks.map((bottleneck, index) => (
                <Group key={index} justify="space-between" mb="xs">
                  <Text size="sm">{bottleneck.stateName}</Text>
                  <Group gap="xs">
                    <Badge color="red">{bottleneck.percentage.toFixed(1)}%</Badge>
                    <Text size="sm">{formatDuration(bottleneck.durationMs)}</Text>
                  </Group>
                </Group>
              ))}
            </Card>
          </Stack>
        )}
      </Modal>

      {/* UX Phase 1: Metric Drill-Down Modal */}
      {selectedMetric && (
        <MetricDrillDownModal
          opened={drillDownModalOpen}
          onClose={() => {
            setDrillDownModalOpen(false);
            setSelectedMetric(null);
          }}
          metricId={selectedMetric.id}
          metricName={selectedMetric.name}
          timeRange={`last-${days}-days`}
        />
      )}
    </Stack>
  );
}

/**
 * Helper function to convert analytics to CSV
 */
function convertAnalyticsToCSV(analytics: any): string {
  if (!analytics) return '';

  const headers = ['Date', 'Total Executions', 'Success Count', 'Failure Count', 'Avg Duration (ms)'];
  const rows = analytics.executionsByDay.map((day: any) => [
    day.date,
    day.count,
    day.successCount,
    day.failureCount,
    day.averageDurationMs,
  ]);

  const csv = [headers.join(','), ...rows.map((row: any[]) => row.join(','))].join('\n');
  return csv;
}

export default ExecutionAnalyticsDashboard;
